import {
  ACTIVE_CLAIM_STATUSES,
  SALES_GOALS,
  clamp01,
  type Availability,
  type ClaimStatus,
  type FitWeights,
  type SalesClaim,
  type SalesGoal,
  type SalesSubject,
} from '@/client/types/sales';

export function lockingClaimFor(subjectId: string, claims: SalesClaim[]): SalesClaim | undefined {
  return claims.find((c) => c.subjectId === subjectId && ACTIVE_CLAIM_STATUSES.includes(c.status));
}

export function achievedGoalsFor(subjectId: string, claims: SalesClaim[]): SalesGoal[] {
  const goals = new Set<SalesGoal>();
  for (const c of claims) {
    if (c.subjectId === subjectId && c.status === 'won' && c.win) goals.add(c.win.goal);
  }
  return SALES_GOALS.filter((g) => goals.has(g));
}

export function availabilityFor(subject: SalesSubject, claims: SalesClaim[], at = Date.now()): Availability {
  if (subject.availability) return subject.availability;
  if (subject.doNotContact) return 'do_not_contact';
  if (lockingClaimFor(subject.id, claims)) return 'locked';
  if (subject.subjectCooldownUntil && new Date(subject.subjectCooldownUntil).getTime() > at) return 'cooling';
  return 'available';
}

export function displayClaimStatus(claim: SalesClaim, at = Date.now()): ClaimStatus {
  if (ACTIVE_CLAIM_STATUSES.includes(claim.status) && new Date(claim.expiresAt).getTime() <= at) return 'expired';
  return claim.status;
}

export function fitScoreFor(
  subject: SalesSubject,
  goal: SalesGoal,
  claims: SalesClaim[],
  weights: FitWeights,
): number {
  const achieved = achievedGoalsFor(subject.id, claims);
  const goalGap = achieved.includes(goal) ? 0 : 1;
  const wsum = weights.areaDemand + weights.activityRecency + weights.spendBand + weights.goalGap || 1;
  const raw =
    (weights.areaDemand * (subject.fitParts?.areaDemand ?? 0) +
      weights.activityRecency * (subject.fitParts?.activityRecency ?? 0) +
      weights.spendBand * (subject.fitParts?.spendBand ?? 0) +
      weights.goalGap * goalGap) /
    wsum;
  return clamp01(raw);
}

export function bestFitFor(subject: SalesSubject, claims: SalesClaim[], weights: FitWeights) {
  const remaining = SALES_GOALS.filter((g) => !achievedGoalsFor(subject.id, claims).includes(g));
  const goals = remaining.length ? remaining : SALES_GOALS;
  let best = { goal: goals[0], score: 0 };
  for (const g of goals) {
    const score = fitScoreFor(subject, g, claims, weights);
    if (score > best.score) best = { goal: g, score };
  }
  return best;
}

export function defaultNextStepDue(hours = 24): string {
  return new Date(Date.now() + hours * 3600_000).toISOString();
}

export function requireActingRepId(repId: string | null | undefined): string {
  if (!repId) {
    const err = new Error('Act as someone first.');
    (err as Error & { code: string }).code = 'no_actor';
    throw err;
  }
  return repId;
}
